import { useCallback, useMemo } from "react";
import styles from "../OnboardingPage.module.scss";
import { StripePriceAnonymous } from "@switcherstudio/switcher-api-client";
import { Sidebar } from "../Sidebar";
import { OnboardingProgressBar } from "components/widgets/progress-bar/OnboardingProgressBar";
import { SidebarContent } from "../Sidebar/SidebarContent";
import { StepContent } from "../stepContent";
import SubscribeFlow from "../stepContent/SubscribeFlow";
import NoSubscribeFlow from "../stepContent/NoSubscribeFlow";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";

const OnboardingContent = ({
    couponHook,
    stepsHook,
    tag,
    defaultToSubscribeFlow,
    setSelectedPlan,
    selectedPlan,
    isTrialing,
    clientId,
    redirectStatus,
    disableChangePlan,
    isGrowUser
}) => {
    const { resellerInventoryItem, removeCoupon } = couponHook;
    const {
        step,
        nextStep,
        isLastStep,
        firstStep,
        total: stepsTotal
    } = stepsHook;

    const onSelectPlanButtonClicked = useCallback(
        (plan: StripePriceAnonymous) => {
            setSelectedPlan(plan);
            nextStep();
        },
        [nextStep, setSelectedPlan]
    );

    const changePlan = useCallback(() => {
        setSelectedPlan(undefined);
        if (resellerInventoryItem?.ResellerInventory?.Price) {
            removeCoupon();
        }
        window.location.href = "https://www.switcherstudio.com/pricing";
    }, [
        removeCoupon,
        resellerInventoryItem?.ResellerInventory?.Price,
        setSelectedPlan
    ]);

    const _isTrialing = useMemo<boolean>(() => {
        // If no plan is selected, we cannot be trialing.
        if (!selectedPlan?.IsRecurring) return false;
        // If a reseller inventory item is present, use its trial days to determine if trialing.
        // Otherwise, fall back to the isTrialing prop.
        return resellerInventoryItem
            ? resellerInventoryItem?.ResellerInventory?.TrialDays !== 0
            : isTrialing;
    }, [isTrialing, resellerInventoryItem, selectedPlan]);

    // We need the "planId" to be set before we check for "isGrowUser".
    // There are several "userInfo" refreshes and only the second has
    // the "planId" in place. "isGrowUser" is inaccurate before then.
    const planId = useSelector(
        (state: RootState) => state.user?.userInfo?.Plan?.Details?.Id
    );

    return (
        <span className={styles["content-container"]}>
            <Sidebar isLastStep={isLastStep} isTrialing={_isTrialing}>
                <OnboardingProgressBar
                    minValue={firstStep - 1}
                    maxValue={stepsTotal}
                    currentValue={step}
                />
                <SidebarContent
                    step={step}
                    defaultToSubscribeFlow={defaultToSubscribeFlow}
                    plan={selectedPlan}
                    resellerInventoryItem={resellerInventoryItem}
                    isTrialing={_isTrialing}
                    changePlan={changePlan}
                    disableChangePlan={disableChangePlan}
                    isGrowUser={planId ? isGrowUser : undefined}
                />
            </Sidebar>
            <StepContent>
                {defaultToSubscribeFlow ? (
                    <SubscribeFlow
                        couponHook={couponHook}
                        stepsHook={stepsHook}
                        onSelectPlanButtonClicked={onSelectPlanButtonClicked}
                        selectedPlan={selectedPlan}
                        clientId={clientId}
                        redirectStatus={redirectStatus}
                        defaultToSubscribeFlow={defaultToSubscribeFlow}
                        isTrialing={_isTrialing}
                        tag={tag}
                        isGrowUser={planId ? isGrowUser : undefined}
                    />
                ) : (
                    <NoSubscribeFlow
                        couponHook={couponHook}
                        stepsHook={stepsHook}
                        clientId={clientId}
                        redirectStatus={redirectStatus}
                        tag={tag}
                        defaultToSubscribeFlow={defaultToSubscribeFlow}
                        isGrowUser={planId ? isGrowUser : undefined}
                    />
                )}
            </StepContent>
        </span>
    );
};

export default OnboardingContent;
